import React, { useState } from "react";
import { Link } from "react-router-dom"; // ✅ For navigation
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { ReactComponent as LoginIcon } from "feather-icons/dist/icons/log-in.svg";
import WordSpacedText from "../components/WordSpacedText";

// ✅ Use your own logo
const logo = process.env.PUBLIC_URL + "/logo512.png";

const Container = tw(ContainerBase)`min-h-screen bg-primary-900 text-white font-medium flex justify-center -m-8`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-12 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold`;
const FormContainer = tw.div`w-full flex-1 mt-8`;

const Form = tw.form`mx-auto max-w-xs`;
const Input = tw.input`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-semibold text-white w-full py-4 rounded-lg transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none`}
  background-color: var(--primary-color);
  &:hover {
    background-color: #9e6e33;
  }
  .icon {
    ${tw`w-6 h-6 -ml-2`}
  }
  .text {
    ${tw`ml-3`}
  }
`;

const IllustrationContainer = tw.div`sm:rounded-r-lg flex-1 bg-purple-100 text-center hidden lg:flex justify-center items-center`;
const IllustrationLogo = tw.img`
  w-96 h-96 
  md:w-[28rem] md:h-[28rem] 
  lg:w-[32rem] lg:h-[32rem] 
  object-contain
`;

export default ({
  logoLinkUrl = "#",
  illustrationImageSrc = process.env.PUBLIC_URL + "/logo512.png", // ✅ Use logo or any right-side image
  headingText = "Sign In To Jamia Website",
  submitButtonText = "Sign In",
  SubmitButtonIcon = LoginIcon,
  forgotPasswordUrl = "#",
  signupUrl = "/signup"
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        setSuccess(true);
        setEmail("");
        setPassword("");
      } else {
        const data = await response.json();
        setError(data.message || "Login failed. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimationRevealPage>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href={logoLinkUrl}>
              <LogoImage src={logo} alt="App Logo" style={{ height: "60px" }} />
            </LogoLink>
            <MainContent>
              <WordSpacedText><Heading>{headingText}</Heading></WordSpacedText>
              <FormContainer>
                <Form onSubmit={handleSubmit}>
                  <Input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <Input
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <SubmitButton type="submit" disabled={loading}>
                    <SubmitButtonIcon className="icon" />
                    <span className="text">{loading ? "Signing In..." : submitButtonText}</span>
                  </SubmitButton>
                </Form>
                {error && <p tw="mt-4 text-red-500 text-center">{error}</p>}
                {success && <p tw="mt-4 text-green-500 text-center">Login successful!</p>}

                <p tw="mt-6 text-xs text-gray-600 text-center">
                  <WordSpacedText>
                    <a href={forgotPasswordUrl} tw="border-b border-gray-500 border-dotted">
                      Forgot Password?
                    </a>
                  </WordSpacedText>
                </p>

                <WordSpacedText>
                  <p tw="mt-8 text-sm text-gray-600 text-center">
                    Don't have an account?{" "}
                    <Link to={signupUrl} tw="border-b border-gray-500 border-dotted">
                      Sign Up
                    </Link>
                  </p>
                </WordSpacedText>
              </FormContainer>
            </MainContent>
          </MainContainer>

          {/* Right side image */}
          <IllustrationContainer>
            <IllustrationLogo src={illustrationImageSrc} alt="Jamia Illustration" />
          </IllustrationContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
};
